// ── STATE ──────────────────────────────────────────────────────────────────
// Cross-cutting mutable state shared across every domain.
//
// DB and GRADES are never reassigned (only mutated/read in place), so they're
// exported as plain `const` — window.DB / window.GRADES are set once below and
// stay correct forever because both sides reference the SAME object; a mutation
// like `DB.users = accounts` from anywhere (a migrated module or still-legacy
// code) is visible everywhere immediately.
//
// Everything else here IS reassigned wholesale at various points (login,
// logout, navigation, data refresh). ES module bindings are read-only outside
// the module that declares them, so each gets a setter function. Every
// reassignment site anywhere in the app goes through these setters now;
// nothing else is allowed to write these directly.

export let PROGRAMS = [];
export let SCHOOLS = [];
export let STORE_SALES = [];
export let COACHING_EVENTS = [];
export let COACHING_MINE = { events: [], requests: [] };
// Mirrors the list register.html uses for the same field, so an address typed
// in the portal and one typed at registration can't disagree on format.
export const US_STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'];
export const GRADES = ['Pre-K', 'Kindergarten', '1st', '2nd', '3rd', '4th', '5th', '6th', '7th', '8th', '9th', '10th', '11th', '12th'];

export const DB = {
  users: [],
  children: [],
  charges: [],
  emailLog: [],
  // Settings — admin-managed values surfaced across the portal
  settings: {
    programs: [],
    lateFeeAmount: 5,
    lateFeeGraceDays: 7,
    retryEmailDays: 3,
    birthdayRecipients: {
      company: 'lori@csyp.org, company-staff@csyp.org',
      bway:    'lori@csyp.org, bway-staff@csyp.org'
    },
    birthdayWindowStart: 'Wednesday',
    birthdayWindowEnd: 'Tuesday',
    // ── Registration windows ──
    // Season identified by start year. 2026 = the 2026–27 season.
    upcomingSeasonYear: 2026,
    priorityOpensAt: '2026-04-15T18:00',     // returning families (local datetime)
    priorityClosesAt: null,                  // their re-enrollment deadline, if set
    openEnrollmentOpensAt: '2026-07-02T09:00', // new families (local datetime)
    priorityManualClosed: false,             // manual override to close early
    openEnrollmentManualClosed: false,
    firmaTemplates: {},
    rehearsalScheduleUrls: {},
    parentMeetingInfo: {},
    // ── Email notification templates ──
    // Tokens like {{parent_first_name}} are replaced with real values when sent.
    emailTemplates: [
      // Billing
      { id: 'payment_failed', group: 'Billing', name: 'Payment failed', active: true,
        tokens: ['parent_first_name', 'amount', 'card_last4'],
        subject: 'CSYP: Payment failed — action needed',
        body: 'Hi {{parent_first_name}},\n\nWe weren’t able to process your payment of {{amount}} on the card ending in {{card_last4}}. Please update your payment method or ensure funds are available so we can retry.\n\nYou can update your card anytime from your CSYP portal under Billing.\n\nThank you,\nCenter Stage Youth Performers' },
      { id: 'payment_retry', group: 'Billing', name: 'Payment reminder', active: true,
        tokens: ['parent_first_name', 'amount', 'retry_days'],
        subject: 'CSYP: Reminder — payment still due',
        body: 'Hi {{parent_first_name}},\n\nThis is a friendly reminder that a payment of {{amount}} is still outstanding on your account. We’ll attempt to charge your card on file again in {{retry_days}} days.\n\nIf you’ve already resolved this, thank you — no further action is needed.\n\nWarmly,\nCenter Stage Youth Performers' },
      { id: 'late_fee', group: 'Billing', name: 'Late fee applied', active: true,
        tokens: ['parent_first_name', 'late_fee_amount', 'grace_days'],
        subject: 'CSYP: A late fee has been applied to your account',
        body: 'Hi {{parent_first_name}},\n\nBecause a balance remained unpaid for more than {{grace_days}} days, a late fee of {{late_fee_amount}} has been added to your account. A late fee of {{late_fee_amount}} will continue to be added each week until the balance is paid in full.\n\nPlease log in to your CSYP portal to settle the balance. If you have questions or need to make arrangements, just reply to this email.\n\nThank you,\nCenter Stage Youth Performers' },
      { id: 'card_expiring', group: 'Billing', name: 'Card expiring', active: true,
        tokens: ['parent_first_name', 'card_last4', 'card_exp'],
        subject: 'CSYP: Your payment card expires this month',
        body: 'Hi {{parent_first_name}},\n\nThe card we have on file ending in {{card_last4}} expires {{card_exp}}. To avoid any interruption to your tuition payments, please update your card in your CSYP portal under Billing.\n\nThank you,\nCenter Stage Youth Performers' },
      // Account
      { id: 'account_invite', group: 'Account', name: 'Account invitation', active: true,
        tokens: ['parent_first_name', 'login_link', 'temp_password'],
        subject: 'CSYP: Your Center Stage Youth Performers account is ready',
        body: 'Hi {{parent_first_name}},\n\nAn account has been created for you in the Center Stage Youth Performers portal. You can sign in here:\n\n{{login_link}}\n\nUse this temporary password to log in for the first time:\n\n{{temp_password}}\n\nFor your security, you’ll be asked to choose a new password right after you sign in. If you have any questions, just reply to this email.\n\nWelcome to CSYP!\nCenter Stage Youth Performers' },
      { id: 'password_reset', group: 'Account', name: 'Password reset', active: true,
        tokens: ['parent_first_name', 'reset_link'],
        subject: 'CSYP: Reset your password',
        body: 'Hi {{parent_first_name}},\n\nWe received a request to reset your CSYP portal password. Click the link below to choose a new one:\n\n{{reset_link}}\n\nIf you didn’t request this, you can safely ignore this email — your password won’t change.\n\nCenter Stage Youth Performers' },
      { id: 'password_changed', group: 'Account', name: 'Password changed', active: true,
        tokens: ['parent_first_name'],
        subject: 'CSYP: Your password has been changed',
        body: 'Hi {{parent_first_name}},\n\nThis is a confirmation that your CSYP portal password was just changed. If this was you, no action is needed.\n\nIf you didn’t make this change, please contact us right away.\n\nCenter Stage Youth Performers' },
      // Registration
      { id: 'registration_reviewed', group: 'Registration', name: 'Registration reviewed (summary)', active: true,
        tokens: ['parent_first_name', 'season', 'decision_summary', 'contract_links'],
        subject: 'CSYP: Your {{season}} registration update',
        body: 'Hi {{parent_first_name}},\n\nThank you for registering with Center Stage Youth Performers for the {{season}} season. Here’s the status of your performer(s):\n\n{{decision_summary}}\n\nFor any performer who was approved, please sign the season contract using the link(s) below. Once signed, we’ll collect the materials fee to reserve their spot:\n\n{{contract_links}}\n\nWarmly,\nCenter Stage Youth Performers' },
      { id: 'registration_approved', group: 'Registration', name: 'Contract ready to sign', active: true,
        tokens: ['parent_first_name', 'program', 'season', 'contract_link'],
        subject: 'CSYP: Sign your {{program}} contract for the {{season}} season',
        body: 'Hi {{parent_first_name}},\n\nYour {{program}} contract for the {{season}} season is ready to sign:\n\n{{contract_link}}\n\nOnce it’s signed, we’ll collect the materials fee to reserve your performer’s spot.\n\nThank you,\nCenter Stage Youth Performers' },
      { id: 'registration_waitlisted', group: 'Registration', name: 'Waitlisted', active: true,
        tokens: ['parent_first_name', 'performer_name', 'program', 'season'],
        subject: 'CSYP: {{performer_name}} — {{program}} waitlist',
        body: 'Hi {{parent_first_name}},\n\nThank you for registering {{performer_name}} for {{program}} this {{season}} season. At the moment the group is full, so {{performer_name}} has been placed on our waitlist.\n\nWe’ll reach out as soon as a spot opens up. Thank you for your patience and interest in CSYP.\n\nWarmly,\nCenter Stage Youth Performers' },
      { id: 'registration_waitlist_approved', group: 'Registration', name: 'Approved off the waitlist', active: true,
        tokens: ['parent_first_name', 'performer_name', 'program', 'season', 'contract_link'],
        subject: 'CSYP: Good news — {{performer_name}} has a spot in {{program}}!',
        body: 'Hi {{parent_first_name}},\n\nGreat news — a spot has opened up and {{performer_name}} is now approved for {{program}} for the {{season}} season!\n\nTo reserve the spot, please sign the season contract using the link below. Once it’s signed, we’ll collect the materials fee:\n\n{{contract_link}}\n\nWe can’t wait to have {{performer_name}} on stage!\n\nCenter Stage Youth Performers' },
      { id: 'materials_fee_due', group: 'Registration', name: 'Materials fee due', active: true,
        tokens: ['parent_first_name', 'season', 'fee_breakdown', 'fee_total'],
        subject: 'CSYP: Materials fee due to reserve your spot',
        body: 'Hi {{parent_first_name}},\n\nThank you for signing your {{season}} contract(s)! The last step to reserve your performer’s spot is the materials fee:\n\n{{fee_breakdown}}\n\nTotal due: {{fee_total}}\n\nYou can pay securely in the family portal — sign in, and you’ll see the balance on your dashboard and under Billing. If you haven’t added a card yet, you’ll be prompted to do so.\n\nThank you,\nCenter Stage Youth Performers' },
      { id: 'registration_declined', group: 'Registration', name: 'Declined', active: true,
        tokens: ['parent_first_name', 'performer_name', 'program', 'season'],
        subject: 'CSYP: Your {{season}} registration',
        body: 'Hi {{parent_first_name}},\n\nThank you for your interest in {{program}} for the {{season}} season. Unfortunately we’re unable to offer {{performer_name}} a spot at this time.\n\nWe truly appreciate your interest in Center Stage Youth Performers and hope you’ll consider us again. Please don’t hesitate to reach out with any questions.\n\nWarmly,\nCenter Stage Youth Performers' }
    ]
  },
  seasonEnrollments: [],
  registrations: [],
  contracts: []
};

export function setPrograms(v)    { PROGRAMS = v; }
export function setSchools(v)     { SCHOOLS = v; }
export function setStoreSales(v)  { STORE_SALES = v; }
export function setCoachingEvents(v) { COACHING_EVENTS = v; }
export function setCoachingMine(v)   { COACHING_MINE = v; }

// Wire PROGRAMS to the live mutable array in settings.
setPrograms(DB.settings.programs);

export let session = null;
export function setSession(v) { session = v; }

export let csrfToken = '';
export function setCsrfToken(v) { csrfToken = v; }

export let sessionMode = 'admin'; // 'admin' | 'parent' for dual-role users
// portal.html has a static onclick="route(sessionMode==='admin'?...)" attribute
// that reads this as a bare global at click time (not template-interpolated
// like everything else), so it stays mirrored onto window.
export function setSessionMode(v) { sessionMode = v; window.sessionMode = sessionMode; }
window.sessionMode = sessionMode;

export let currentView = null;
export function setCurrentView(v) { currentView = v; }

export let settingsDirty = false;
export function setSettingsDirty(v) { settingsDirty = v; }

export let currentParentId = null;
export function setCurrentParentId(v) { currentParentId = v; }

export let currentPerformerId = null;
export function setCurrentPerformerId(v) { currentPerformerId = v; }

// Which help guide the reader view (a-help-doc) is showing. Lives here rather
// than in views/admin-help.js because it rides in the URL hash the same way
// the two detail views' ids do — routing.js writes it, auth.js restores it on
// a deep link.
export let currentHelpSlug = null;
export function setCurrentHelpSlug(v) { currentHelpSlug = v; }

// Non-null while an admin is "viewing as" a parent (read-only) — holds the
// real admin's session object so it can be restored on exit.
export let viewingAsAdmin = null;
export function setViewingAsAdmin(v) { viewingAsAdmin = v; }
